import type { Command } from 'commander';
import { randomUUID } from 'node:crypto';
import { unwrapDataResponse } from '@wport/core';
import { enterpriseDelete, enterprisePatch, enterprisePost } from '../../../lib/enterprise-client';
import { resolveApiKey } from '../../../lib/credentials-store';
import { resolveContext } from '../../../lib/global-opts';
import { CliError, ExitCode } from '../../../lib/errors';
import { printJson } from '../../../lib/output';
import type { CreatedJob } from './create';
import { requireEncId, type WriteCtx } from './write-shared';

interface LifecycleFlags {
	idempotencyKey?: string;
	confirm?: boolean;
}

/** publish / unpublish 共用：PATCH /jobs/:enc_id/{action}（空 body，帶 Idempotency-Key）。 */
export async function runJobsTransition(
	ctx: WriteCtx,
	apiKey: string,
	encId: string,
	action: 'publish' | 'unpublish',
	idempotencyKey: string
): Promise<void> {
	const trimmed = requireEncId(encId);
	const { body } = await enterprisePatch(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		`/jobs/${encodeURIComponent(trimmed)}/${action}`,
		{},
		{ idempotencyKey }
	);
	const result = unwrapDataResponse<CreatedJob>(body);
	if (ctx.format === 'json') {
		printJson(result);
		return;
	}
	const verb = action === 'publish' ? 'Published' : 'Unpublished';
	process.stdout.write(`${verb} job: ${result.enc_id ?? trimmed}\n`);
}

/**
 * `jobs delete` 核心：破壞性寫入 —— 無 `--confirm` 一律本地 exit 2、**不發請求**（PRD §6.2）。
 * 帶 Idempotency-Key（後端強制）。
 */
export async function runJobsDelete(
	ctx: WriteCtx,
	apiKey: string,
	encId: string,
	confirm: boolean,
	idempotencyKey: string
): Promise<void> {
	if (!confirm) {
		throw new CliError('Refusing to delete without --confirm (destructive, irreversible)', ExitCode.InvalidArgument);
	}
	const trimmed = requireEncId(encId);
	await enterpriseDelete(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		`/jobs/${encodeURIComponent(trimmed)}`,
		{ idempotencyKey }
	);
	// DELETE 後端回 `DataResponse(null, ...)`（data 為 null、無 enc_id）→ 不 unwrap/deref（否則
	// null.enc_id 在 table 模式下丟 TypeError）；用呼叫時的 enc_id 回報刪除結果。
	if (ctx.format === 'json') {
		printJson({ enc_id: trimmed, deleted: true });
		return;
	}
	process.stdout.write(`Deleted job: ${trimmed}\n`);
}

/**
 * `jobs copy` 核心：POST /jobs/:enc_id/copy（空 body，帶 Idempotency-Key）→ 回新職缺
 * { enc_id, code, created_at }（新職缺為未刊登草稿）。後端強制 Idempotency-Key。
 */
export async function runJobsCopy(ctx: WriteCtx, apiKey: string, encId: string, idempotencyKey: string): Promise<void> {
	const trimmed = requireEncId(encId);
	const { body } = await enterprisePost(
		{ baseUrl: ctx.baseUrl, locale: ctx.locale, timeoutMs: ctx.timeoutMs, apiKey },
		`/jobs/${encodeURIComponent(trimmed)}/copy`,
		{},
		{ idempotencyKey }
	);
	const result = unwrapDataResponse<CreatedJob>(body);
	if (ctx.format === 'json') {
		printJson(result);
		return;
	}
	process.stdout.write(`Copied job ${trimmed} → new draft: ${result.enc_id ?? '(unknown)'}\n`);
	// issue #106 P1-3：legacy 來源子表為空時，後端於 copy 回應帶 incomplete_fields。人類模式明示，
	// 讓使用者知道 publish 前要補哪些欄（json 模式已含此欄，不重複印）。defensive 讀，欄位缺席即略過。
	const incomplete = extractStringArray(result.incomplete_fields);
	if (incomplete.length > 0) {
		process.stdout.write(`  ⚠ Incomplete for publish — fill before \`jobs publish\`: ${incomplete.join(', ')}\n`);
	}
}

/** defensive：把 unknown 收成 string[]（非陣列 / 非字串元素一律濾掉）。 */
function extractStringArray(value: unknown): string[] {
	if (!Array.isArray(value)) return [];
	return value.filter((v): v is string => typeof v === 'string');
}

export function registerEnterpriseJobsCopy(parent: Command): void {
	parent
		.command('copy <enc_id>')
		.description('Copy a job posting into a new unpublished draft')
		.option('--idempotency-key <key>', 'reuse across retries (default: a fresh UUID)')
		.action(async (encId: string, flags: LifecycleFlags, command: Command) => {
			const ctx = resolveContext(command);
			const { key } = resolveApiKey((command.optsWithGlobals() as { apiKey?: string }).apiKey);
			await runJobsCopy(ctx, key, encId, flags.idempotencyKey ?? randomUUID());
		});
}

/**
 * `jobs close` 已於 0.7.0 全面撤銷（產品決議：職缺狀態只有上下架，無「關閉」態；
 * GUI 不存在此概念，CLI 關閉的職缺會在企業後台卡成無法操作的殭屍列）。
 * 保留隱藏 stub：老腳本/agent 打到時給明確遷移訊息並 exit 2、**不發請求**。
 */
export const JOBS_CLOSE_REMOVED_MESSAGE =
	'`jobs close` was removed in @wport/cli 0.7.0 — the "closed" job state has been revoked product-wide ' +
	'(jobs are only published/unpublished). Use `jobs unpublish <enc_id>` to take a job off the board, ' +
	'or `jobs delete <enc_id> --confirm` to remove it.';

export function registerEnterpriseJobsClose(parent: Command): void {
	parent
		.command('close [enc_id]', { hidden: true })
		.description('(removed in 0.7.0)')
		.allowUnknownOption(true)
		.action(async () => {
			throw new CliError(JOBS_CLOSE_REMOVED_MESSAGE, ExitCode.InvalidArgument);
		});
}

export function registerEnterpriseJobsPublish(parent: Command): void {
	parent
		.command('publish <enc_id>')
		.description('Publish a job posting')
		.option('--idempotency-key <key>', 'reuse across retries (default: a fresh UUID)')
		.action(async (encId: string, flags: LifecycleFlags, command: Command) => {
			const ctx = resolveContext(command);
			const { key } = resolveApiKey((command.optsWithGlobals() as { apiKey?: string }).apiKey);
			await runJobsTransition(ctx, key, encId, 'publish', flags.idempotencyKey ?? randomUUID());
		});
}

export function registerEnterpriseJobsUnpublish(parent: Command): void {
	parent
		.command('unpublish <enc_id>')
		.description('Unpublish (take down) a job posting')
		.option('--idempotency-key <key>', 'reuse across retries (default: a fresh UUID)')
		.action(async (encId: string, flags: LifecycleFlags, command: Command) => {
			const ctx = resolveContext(command);
			const { key } = resolveApiKey((command.optsWithGlobals() as { apiKey?: string }).apiKey);
			await runJobsTransition(ctx, key, encId, 'unpublish', flags.idempotencyKey ?? randomUUID());
		});
}

export function registerEnterpriseJobsDelete(parent: Command): void {
	parent
		.command('delete <enc_id>')
		.description('Delete a job posting (destructive; requires --confirm)')
		.option('--confirm', 'confirm this destructive, irreversible delete')
		.option('--idempotency-key <key>', 'reuse across retries (default: a fresh UUID)')
		.action(async (encId: string, flags: LifecycleFlags, command: Command) => {
			const ctx = resolveContext(command);
			const { key } = resolveApiKey((command.optsWithGlobals() as { apiKey?: string }).apiKey);
			await runJobsDelete(ctx, key, encId, flags.confirm === true, flags.idempotencyKey ?? randomUUID());
		});
}
